import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getInvoiceWaste } from "../../../services";
import { InvoiceWaste, InvoiceWasteSummary } from "../../../types/invoice-waste";
import { useUserRole } from "../../../utils/hooks/useUserRole";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InvoiceDetailsSkeleton from "./InvoiceDetailsSkeleton";
import Typography from "@mui/material/Typography";
import { SxProps, Theme } from "@mui/material/styles";
import { getReconcileEditDisabledReason } from "./reconcile";
import { sumInvoiceWasteColumns } from "./invoiceWasteTotals";
import { formatNumber, numberWithCommas } from "../../../utils";

const yellowBorder = "1px solid #FFE941";
const yellowBg = "#FFE9411A";
const redBorder = "1px solid #FB3B3B";
const redBg = "#FB3B3B1A";
const COLUMN_COUNT = 16;

/**
 * The แถวสรุปยอด row: a rule above it and bold figures, matching the totals row the
 * รายงานการโอน tables already close with.
 *
 * It is sticky because this table is both wider and taller than the viewport — an
 * un-pinned total sits below the last product, which on a full catalogue means
 * scrolling past every row to read the number you came for.
 */
const TOTALS_ROW_SX = {
  position: "sticky" as const,
  bottom: 0,
  zIndex: 2,
  "& td": {
    bgcolor: (theme: Theme) => theme.palette.background.default,
    borderTop: "2px solid",
    borderTopColor: "divider",
    borderBottom: "none",
    fontWeight: 700,
    whiteSpace: "nowrap",
    padding: "8px 4px",
  },
};

// Columns from "ยกมา" through "ควรเบิก" share one equal width.
const COL_WIDTH = 96;
const INDEX_COL_WIDTH = 56;
const NAME_COL_WIDTH = 220;
const NOTE_COL_WIDTH = 160;
const EQUAL_WIDTH_COLS = [
  "carryover_qty",
  "receive_withdraw_qty",
  "actual_remain_qty",
  "receive_remain_qty",
  "receive_waste_qty",
  "actual_waste_qty",
  "transfer_in",
  "transfer_out",
  "defective",
  "actual_sell_qty",
  "sell_pda_qty",
  "fine",
  "should_withdraw_qty",
];
const TABLE_MIN_WIDTH =
  INDEX_COL_WIDTH +
  NAME_COL_WIDTH +
  EQUAL_WIDTH_COLS.length * COL_WIDTH +
  NOTE_COL_WIDTH;

interface InvoiceDetailsListProps {
  reportId?: string;
  reconcileId: string;
  reportCode: string;
  versionHistory: string;
  triggerFetch: number;
  // Linked Sale Daily Report status, used to decide if editing is still allowed.
  saleReportStatus?: string;
  // ค่าปรับ is NOT the column's sum — see sumInvoiceWasteColumns. It is netted per price
  // group server-side, on the same reconcile version this table is showing. Fetched by
  // the page because the ladder it comes with opens from the tab bar, not from here.
  wasteSummary: InvoiceWasteSummary | null;
}

export default function InvoiceDetailsList({
  reportId,
  reconcileId,
  reportCode,
  versionHistory,
  triggerFetch,
  saleReportStatus,
  wasteSummary,
}: InvoiceDetailsListProps) {
  const { isHeadOfficeAccounting } = useUserRole();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [invoiceWaste, setInvoiceWaste] = useState<InvoiceWaste[]>();
  const [reconcileStatus, setReconcileStatus] = useState<string | null>(null);

  const onGetInvoiceWaste = useCallback(
    async (uuid: string, version: string) => {
      setLoading(true);
      try {
        const { data: body } = await getInvoiceWaste(uuid, version);
        setInvoiceWaste(body.data ?? []);
        setReconcileStatus(body.reconcile_status ?? null);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (reportId && versionHistory) {
      onGetInvoiceWaste(reportId, versionHistory);
    }
  }, [triggerFetch, reportId, versionHistory, onGetInvoiceWaste]);

  const headCellSx: SxProps<Theme> = {
    borderRight: (theme) => `1px solid ${theme.palette.draft.main}`,
    bgcolor: (theme) => theme.palette.background.default,
  };

  // Editing is gated to mirror the backend 409 validation on
  // PUT /reconciles/{uuid}/details — see ./reconcile.
  const editDisabledReason = getReconcileEditDisabledReason({
    reconcileStatus,
    saleReportStatus,
  });

  const totals = sumInvoiceWasteColumns(invoiceWaste);

  // An empty versionHistory means the reconcile version is still being resolved
  // (e.g. right after switching to this tab). Show the skeleton instead of an
  // empty "ไม่พบข้อมูล" table, and avoid rendering against a not-yet-known version.
  if (loading || !versionHistory) return <InvoiceDetailsSkeleton />;

  return (
    <>
      {!isHeadOfficeAccounting && invoiceWaste && (
        // A disabled button does not emit hover events, so the Tooltip wraps a
        // span. The span also carries the absolute positioning to keep the
        // button in the exact same place as before.
        <Tooltip title={editDisabledReason ?? ""}>
          <Box
            component="span"
            sx={{ position: "absolute", top: "4px", right: "16px" }}
          >
            <Button
              variant="outlined"
              startIcon={<EditOutlinedIcon />}
              disabled={Boolean(editDisabledReason)}
              onClick={() =>
                navigate(
                  `/report/sale/details/${reportId}/${reconcileId}/${reportCode}/${versionHistory}/fix-waste`,
                )
              }
            >
              แก้ไขยอดของเสีย
            </Button>
          </Box>
        </Tooltip>
      )}
      <TableContainer sx={{ borderRadius: "12px", overflowX: "auto" }}>
        <Table sx={{ tableLayout: "fixed", minWidth: TABLE_MIN_WIDTH }}>
          <colgroup>
            <col style={{ width: INDEX_COL_WIDTH }} />
            <col style={{ width: NAME_COL_WIDTH }} />
            {EQUAL_WIDTH_COLS.map((key) => (
              <col key={key} style={{ width: COL_WIDTH }} />
            ))}
            <col style={{ width: NOTE_COL_WIDTH }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell align="left" sx={headCellSx}>
                ลำดับ
              </TableCell>
              <TableCell align="left" sx={headCellSx}>
                รายการ
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                ยกมา
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                เบิก
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  border: yellowBorder,
                  bgcolor: yellowBg,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                เหลือ
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                เหลือ (PDA)
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                เสีย (PDA)
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  border: redBorder,
                  bgcolor: redBg,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                เสีย
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                โอนเข้า
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                โอนออก
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                ชำรุด
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                ขายได้
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                ขาย (PDA)
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                ค่าปรับ
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                ควรเบิก
              </TableCell>
              <TableCell align="left" sx={headCellSx}>
                หมายเหตุ
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(!invoiceWaste || invoiceWaste.length === 0) && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบข้อมูล
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {invoiceWaste &&
              invoiceWaste.map((product, index) => (
                <TableRow
                  key={product.product_uuid}
                  sx={{
                    bgcolor: index % 2 === 1 ? "#f6f6f9" : "#fff",
                    "& td, & th": { padding: "4px" },
                  }}
                >
                  <TableCell align="center">{index + 1}</TableCell>
                  <TableCell align="left">
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {product.product_name}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {product.actual_carryover_qty ??
                      product.receive_carryover_qty ??
                      product.transfer_qty}
                  </TableCell>
                  <TableCell align="center">
                    {product.actual_withdraw_qty ??
                      product.receive_withdraw_qty ??
                      "-"}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ border: yellowBorder, bgcolor: yellowBg }}
                  >
                    {product.actual_remain_qty}
                  </TableCell>
                  <TableCell align="center">
                    {product.receive_remain_qty ?? "-"}
                  </TableCell>
                  <TableCell align="center">
                    {product.receive_waste_qty ?? "-"}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ border: redBorder, bgcolor: redBg }}
                  >
                    {product.actual_waste_qty}
                  </TableCell>
                  <TableCell align="center">{product.transfer_in ?? "-"}</TableCell>
                  <TableCell align="center">{product.transfer_out ?? "-"}</TableCell>
                  <TableCell align="center">{product.defective ?? "-"}</TableCell>
                  <TableCell align="center">{product.actual_sell_qty ?? "-"}</TableCell>
                  <TableCell align="center">{product.sell_pda_qty ?? "-"}</TableCell>
                  <TableCell align="center">{product.fine ?? "-"}</TableCell>
                  <TableCell align="center">
                    {product.should_withdraw_qty}
                  </TableCell>
                  <TableCell align="center">{product.note || "-"}</TableCell>
                </TableRow>
              ))}
          </TableBody>
          {invoiceWaste && invoiceWaste.length > 0 && (
            <TableFooter>
              <TableRow sx={TOTALS_ROW_SX}>
                <TableCell align="center">รวม</TableCell>
                <TableCell align="left">
                  {numberWithCommas(invoiceWaste.length)} รายการ
                </TableCell>
                <TableCell align="center">{numberWithCommas(totals.carryover)}</TableCell>
                <TableCell align="center">{numberWithCommas(totals.withdraw)}</TableCell>
                <TableCell align="center">{numberWithCommas(totals.remain)}</TableCell>
                <TableCell align="center">{numberWithCommas(totals.remain_pda)}</TableCell>
                <TableCell align="center">{numberWithCommas(totals.waste_pda)}</TableCell>
                <TableCell align="center">{numberWithCommas(totals.waste)}</TableCell>
                <TableCell align="center">{numberWithCommas(totals.transfer_in)}</TableCell>
                <TableCell align="center">{numberWithCommas(totals.transfer_out)}</TableCell>
                <TableCell align="center">{numberWithCommas(totals.defective)}</TableCell>
                <TableCell align="center">{numberWithCommas(totals.sell)}</TableCell>
                <TableCell align="center">{numberWithCommas(totals.sell_pda)}</TableCell>
                <TableCell align="center">
                  {/* Blank until the summary lands rather than a 0 that would read as
                      "nothing was deducted today". */}
                  {wasteSummary ? formatNumber(wasteSummary.total_deduct) : "-"}
                </TableCell>
                <TableCell align="center">
                  {numberWithCommas(totals.should_withdraw)}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </>
  );
}
